import { useCreate, useRedirect } from "ra-core";
import { Link } from "react-router";
import { useState } from "react";
import {
  ArrowLeft,
  Briefcase,
  Ruler,
  DollarSign,
  User,
  Activity,
  MessageSquare,
  Save,
  MapPin,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { JOB_TYPES } from "../constants";
import { JOB_STAGES } from "./jobStages";
import { AddressInput } from "../AddressInput";
import { ContactSearchSelect } from "../contacts/ContactSearchSelect";
import { ContactCreateDialog } from "../contacts/ContactCreateDialog";
import type { Contact } from "../../providers/fakerest/dataGenerator/types";

export const JobCreate = () => {
  const [create, { isPending: isCreating }] = useCreate();
  const redirect = useRedirect();

  const [contactId, setContactId] = useState<string>("");
  const [address, setAddress] = useState("");
  const [jobType, setJobType] = useState<string>("roof_replacement");
  const [roofSqft, setRoofSqft] = useState<string>("");
  const [estimatedValue, setEstimatedValue] = useState<string>("");
  const [status, setStatus] = useState<string>("new_lead");
  const [notes, setNotes] = useState("");
  const [contactDialogOpen, setContactDialogOpen] = useState(false);

  const handleContactCreated = (contact: Contact) => {
    setContactId(String(contact.id));
  };

  const handleSubmit = () => {
    if (!contactId) {
      alert("Please select a contact");
      return;
    }
    if (!address.trim()) {
      alert("Please enter an address");
      return;
    }

    create(
      "jobs",
      {
        data: {
          contact_id: Number(contactId),
          address: address.trim(),
          job_type: jobType,
          roof_sqft: Number(roofSqft) || 0,
          estimated_value: Number(estimatedValue) || 0,
          status,
          notes: notes.trim(),
          index: 0,
        },
      },
      {
        onSuccess: (data) => {
          redirect("show", "jobs", data.id);
        },
      },
    );
  };

  return (
    <div className="p-4 max-w-2xl mx-auto">
      {/* Back button */}
      <Link
        to="/jobs"
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Jobs
      </Link>

      {/* Header */}
      <h1 className="text-2xl font-semibold mb-6 flex items-center gap-2">
        <Briefcase className="h-6 w-6" />
        Create New Job
      </h1>

      {/* Form Card */}
      <Card>
        <CardHeader>
          <CardTitle>Job Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Contact */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Contact <span className="required-asterisk">*</span>
            </Label>
            <ContactSearchSelect
              value={contactId}
              onChange={setContactId}
              placeholder="Search for a contact..."
              onCreateNew={() => setContactDialogOpen(true)}
            />
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Address <span className="required-asterisk">*</span>
            </Label>
            <AddressInput
              value={address}
              onChange={setAddress}
              placeholder="123 Main St, City, State 12345"
            />
          </div>

          {/* Job Type */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              Job Type
            </Label>
            <Select value={jobType} onValueChange={setJobType}>
              <SelectTrigger className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {JOB_TYPES.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    <span className="flex items-center gap-2">
                      <type.icon className={`h-4 w-4 ${type.color}`} />
                      {type.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Roof Sq Ft */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Ruler className="h-4 w-4" />
              Roof Sq Ft
            </Label>
            <Input
              type="number"
              value={roofSqft}
              onChange={(e) => setRoofSqft(e.target.value)}
              placeholder="2500"
            />
          </div>

          {/* Estimated Value */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Estimated Value
            </Label>
            <Input
              type="number"
              value={estimatedValue}
              onChange={(e) => setEstimatedValue(e.target.value)}
              placeholder="15000"
            />
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Status
            </Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {JOB_STAGES.map((stage) => (
                  <SelectItem key={stage.value} value={stage.value}>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${stage.color}`} />
                      {stage.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Notes
            </Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes about the job..."
              rows={3}
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-4">
            <Button
              onClick={handleSubmit}
              disabled={isCreating || !contactId || !address.trim()}
            >
              <Save className="h-4 w-4 mr-2" />
              {isCreating ? "Creating..." : "Create Job"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Contact Create Dialog */}
      <ContactCreateDialog
        open={contactDialogOpen}
        onOpenChange={setContactDialogOpen}
        onSuccess={handleContactCreated}
      />
    </div>
  );
};
